import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import moment from 'moment'
import {Container, Row, Col, Dropdown, Modal, Button, Form} from 'react-bootstrap'
import { css } from 'emotion'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEdit, faExclamationTriangle, faFileInvoice, faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import Select from 'app/universal/inputs/Select'
import LeMoulinApi from 'app/universal/utils/LeMoulinApi'
import UpdateDataApi from 'app/universal/utils/UpdateDataApi'
import swal from 'sweetalert';
import { REFUSAL_REASONS } from 'helper/refusalReasons';
import { SHOW_LEGACY_LMVP, openLmvp } from 'helper/lmvpLinks';
import { useAlphaAccess } from 'app/universal/route/withAlphaAccess';

const Order = (props) => {

  const { order, handleOpenModal, handleSelectOrder, handleDeselectOrder, isSelected, nbOrders, groupedOrders: allGroupedOrders } = props

  const parentGroupedOrder = order.grouped_order_id && allGroupedOrders
    ? allGroupedOrders.find(g => g.id === order.grouped_order_id) ?? null
    : null;

  const hasClientPlanning = useAlphaAccess('planning_client');

  const apiBaseUrl = process.env.REACT_APP_API_URL;
  const buildRecapUrl = (token) => token ? `${apiBaseUrl}/api/pro/orders/noauth/recap/${token}` : null;

  const [isLoading, setIsLoading]     = useState(false);
  const [status, setStatus]           = useState(['400', '401', '402', '411', '412'].includes(order?.grouped_order_status ? order.grouped_order_status : order.status) ? '401' : order?.grouped_order_status ? order.grouped_order_status : order.status);
  const [isCancelled, setIsCancelled] = useState(order.cancel_date ? true : false);
  const [cancelDate, setCancelDate]   = useState(order.cancel_date);
  const [info, setInfo]               = useState(order.info);
  const [isRecapModalOpen, setIsRecapModalOpen] = useState(false);
  const [showRefusalModal, setShowRefusalModal]   = useState(false);
  const [refusalReason, setRefusalReason]         = useState('');
  const [refusalReasonDetail, setRefusalReasonDetail] = useState('');
  const [recapEmailContent, setRecapEmailContent] = useState('');
  const [isSendingRecap, setIsSendingRecap] = useState(false);
  const [recapToken, setRecapToken] = useState(order.recap_public_token || null);
  const [recapLink, setRecapLink] = useState(buildRecapUrl(order.recap_public_token));
  const [isGeneratingRecapLink, setIsGeneratingRecapLink] = useState(false);

  const currentUrlEncode = encodeURIComponent(window.location.href);

  const {
    company,
    company_old,
    firstname,
    lastname,
    format,
    participants_number,
    invoice_number,
    confirmed_participants_number,
    modified_since_last_printed,
    id,
    updated_at,
    groupedOrders
  } = order

  const delivery_date = order.delivery ? order.delivery.delivery_date : order.delivery_date;

  let statusOptions = [
    {
      "option"      : "101",
      "traduction"  : 'En attente'
    },
    {
      "option"      : "102",
      "traduction"  : "Validé"
    },
    {
      "option"      : "401",
      "traduction"  : "Refusé par le client"
    }
  ];

  // if (process.env.NODE_ENV !== 'production') {
    statusOptions.push({
      "option"      : "103",
      "traduction"  : "A vérifier avant facturation"
    });
  // }

  let statusOptionsInvoiced = [
    {
      "option"      : "103",
      "traduction"  : "A vérifier avant facturation"
    },
    {
      "option"      : "201",
      "traduction"  : "Créer la facture - A payer"
    }
  ];

  const updateStatus = (event) => {
    const statusValue = event.target.value;
    if (statusValue === status) {
      return;
    }
    // Passage en refusé → on demande d'abord le motif via une popup.
    if (['400', '401', '402', '411', '412'].includes(statusValue)) {
      setRefusalReason('');
      setRefusalReasonDetail('');
      setShowRefusalModal(true);
      return;
    }
    if (statusValue === '201') {
      swal({
        title: "Attention ",
        text: "Vous allez transformer votre devis en facture, cette action est irreversible.",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      })
      .then((confirm) => {
        if (confirm) {
          performStatusUpdate('201');
        }
      });
      return;
    }
    performStatusUpdate(statusValue);
  }

  // Applique le changement de statut (+ motif de refus le cas échéant).
  const performStatusUpdate = (statusValue, reason = null, reasonDetail = null) => {
    setIsLoading(true);
    const api = info.order_type;
    const reloadAfterUpdate = order?.grouped_order_id ? true : false;
    const payload = { status: statusValue };
    if (['400', '401', '402', '411', '412'].includes(statusValue) && reason) {
      payload.refusal_reason = reason;
      payload.refusal_reason_detail = reason === 'autre' ? (reasonDetail || null) : null;
    }
    const put = order?.grouped_order_id
      ? LeMoulinApi.request.grouped.status(order.grouped_order_id, payload)
      : LeMoulinApi.request[api].put(id, payload);
    return put.then(resp => {
      setStatus(['400', '401', '402', '411', '412'].includes(resp.status) ? '401' : resp.status);
      setIsLoading(false);
      if (reloadAfterUpdate) {
        window.location.reload();
      }
    });
  }

  // Validation de la popup motif de refus.
  const confirmRefusal = () => {
    if (!refusalReason) {
      return;
    }
    setShowRefusalModal(false);
    performStatusUpdate('401', refusalReason, refusalReasonDetail);
  }

  const cancelOrder = () => {
    setIsLoading(true);
    const api = info.order_type;
    const reloadAfterUpdate = order?.grouped_order_id ? true : false;
    swal({
      title: "Attention ",
      text: "Annuler cette commande ?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    })
    .then((confirm) => {
      if(confirm){
        let cancelDate = moment().format('YYYY-MM-DD HH:mm:ss');
        const put = order?.grouped_order_id ? LeMoulinApi.request.grouped.put(order.grouped_order_id, {cancel_date : cancelDate}) : LeMoulinApi.request[api].put(id, {cancel_date : cancelDate});
        return put.then(resp => {
          if (resp.success !== undefined && resp.success === false) {
            let msg = "Une erreur est survenue lors de l'annulation de la facture";
            if (resp.message) {
              msg = resp.message;
            }
            swal({
              title: "Attention ",
              text: msg,
              icon: "warning",
              dangerMode: true,
            })           
          }
          else {
            setCancelDate(resp.cancel_date);
            setIsCancelled(resp.cancel_date ? true : false); 
            setInfo(resp.info);
          }
          setIsLoading(false);
          if(reloadAfterUpdate) {
            window.location.reload();
          }
        });
      }else{
        setIsLoading(false);
      }
    });
  }

  const uncancelOrder = () => {
    setIsLoading(true);
    const api = info.order_type;
    const reloadAfterUpdate = order?.grouped_order_id ? true : false;
    swal({
      title: "Attention ",
      text: "Attention cette commande sera de nouveau valide.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    })
    .then((confirm) => {
      if(confirm){
        const put = order?.grouped_order_id ? LeMoulinApi.request.grouped.put(order.grouped_order_id, {cancel_date : null}) : LeMoulinApi.request[api].put(id, {cancel_date : null});
        return put.then(resp => {
          if (resp.success !== undefined &&  resp.success === false) {
            let msg = "Une erreur est survenue lors de l'annulation de la facture";
            if (resp.message) {
              msg = resp.message;
            }
            swal({
              title: "Attention ",
              text: msg,
              icon: "warning",
              dangerMode: true,
            })           
          }
          else {
            setCancelDate(resp.cancel_date);
            setIsCancelled(resp.cancel_date ? true : false); 
            setInfo(resp.info);
          }
          setIsLoading(false);
          if(reloadAfterUpdate) {
            window.location.reload();
          }
        });
      }else{
        setIsLoading(false);
      }
    });
  }

  const handleOpenRecapModal = () => {
    const prestationNumber = order?.info?.orderNumber || order?.id || '';
    const deliveryDateText = delivery_date
      ? moment(delivery_date).locale('fr').format('dddd D MMMM YYYY')
      : '';
    const defaultContent = `Voici le récapitulatif de la prestation ${prestationNumber} pour le ${deliveryDateText}, tu trouveras tous les éléments en suivant le lien ci-dessous :`;
    setRecapEmailContent(defaultContent.trim());
    setIsRecapModalOpen(true);
  }

  const handleCloseRecapModal = () => {
    if (isSendingRecap) {
      return;
    }
    setIsRecapModalOpen(false);
  }

  useEffect(() => {
    const nextToken = order?.recap_public_token || null;
    setRecapToken(nextToken);
    setRecapLink(buildRecapUrl(nextToken));
  }, [order?.recap_public_token]);

  const handleSendRecapEmail = () => {
    if (!recapEmailContent.trim()) {
      return swal({
        title: "Attention ",
        text: "Le contenu de l'email ne peut pas être vide.",
        icon: "warning",
      });
    }
    setIsSendingRecap(true);
    const payload = {
      orderId: order.id,
      groupedOrderId: order?.grouped_order_id,
      variables: {
        modifiedContentText: recapEmailContent
      }
    };
    LeMoulinApi.request.order.sendRecapEmail(payload)
      .then(() => {
        setIsSendingRecap(false);
        setIsRecapModalOpen(false);
        setRecapEmailContent('');
        swal({
          title: "Succès",
          text: "Le récapitulatif a été envoyé.",
          icon: "success"
        });
      })
      .catch(() => {
        setIsSendingRecap(false);
        swal({
          title: "Erreur",
          text: "Impossible d'envoyer le récapitulatif, veuillez réessayer.",
          icon: "error",
          dangerMode: true,
        });
      });
  }

  const handleOpenRecapLink = async (event) => {
    event.preventDefault();

    if (recapLink) {
      window.open(recapLink, '_blank', 'noopener,noreferrer');
      return;
    }

    if (isGeneratingRecapLink) {
      return;
    }

    setIsGeneratingRecapLink(true);
    const orderType = order?.info?.order_type || 'v2';

    try {
      const response = await LeMoulinApi.request.order.generateRecapLink(order.id, orderType);
      const token = response?.token ?? response?.data?.token ?? null;
      const generatedLink = response?.recapUrl ?? response?.data?.recapUrl ?? buildRecapUrl(token);

      if (!token || !generatedLink) {
        throw new Error('missing_token');
      }

      setRecapToken(token);
      setRecapLink(generatedLink);
      window.open(generatedLink, '_blank', 'noopener,noreferrer');
    } catch (error) {
      swal({
        title: "Erreur",
        text: "Impossible de générer le lien récapitulatif.",
        icon: "error",
        dangerMode: true,
      });
    } finally {
      setIsGeneratingRecapLink(false);
    }
  }

  const themeColor = () => {
    if(info.order_type === 'mealtray'){
      return '#a18b6e'
    }
    else if(info.order_type === 'caterer' || order?.prestation_type === 'caterer'){
      return 'black'
    }
    else if(info.order_type === 'breakfast' || order?.prestation_type === 'breakfast'){
      return '#439b5c'
    }
    else if(info.order_type === 'v2' || order?.prestation_type === 'mealtray'){
      return '#2b7ed6'
    }
  }

  const styles = {
    delivery: {
      background: 'white',
      margin: '0.5rem 0',
      border: '1px solid #ddd',
      borderLeft: '10px solid '+themeColor()+'',
      transition: '300ms',
      position: 'relative'
    },
    statut: {
      textTransform: 'uppercase',
      fontSize: '0.6rem',
      marginBottom: '0rem'
    },
    col:{
      padding: '0.5rem',
      borderRight: '1px #dedede solid',
      display: 'flex',
      justifyContent: 'center',
      flexDirection: 'column',
    },
    verticalAlign:{
      display: 'flex',
      flexDirection: 'row',
    },
    para:{
      marginBottom: '0rem',
      fontSize: '0.9rem',
      lineHeight: '1.3'
    },
    center:{
      textAlign: 'center',
      '@media (max-width: 767px)':{
        textAlign: 'left'
      }
    },
    divisor: {
      height: '1px',
      backgroundColor: '#dedede',
      margin: '0.7rem 0'
    },
    borderRight: {
      '@media (max-width: 767px)':{
        borderRight: '1px #dedede solid',
      }
    },
    mobileDivisor: {
      '@media (max-width: 767px)':{
        display: 'none'
      }
    },
    borderBottomMobile:{
      '@media (max-width: 767px)':{
        borderBottom: '1px #dedede solid',
      }
    },
    borderTopMobile:{
      '@media (max-width: 767px)':{
        borderTop: '1px #dedede solid',
      }
    },
    company:{
      textTransform: 'uppercase',
      color: themeColor(),
      fontWeight: 'bold',
      fontSize: '0.8rem',
      margin: '0'
    },
    label2Col:{
      width: '80%',
      display:'inline-block',
      margin:'auto'
    },
    checkbox2Col:{
      width: '20%',
      display:'inline-block',
      borderLeft: '1px solid #dedede',
      margin:'auto'
    },
    button:{
      textWrap: 'wrap',
      width: '100%',
      color: '#222d32',
      textAlign: 'center',
      fontSize: '0.8rem',
      transition: '300ms',
      display: 'block',
      padding: '4px',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      wordBreak: 'break-word',
      ':hover':{
        textDecoration: 'none',
        backgroundColor: '#222d32',
        color: 'white',
      }
    },
    theme:{
      backgroundColor: themeColor(),
      color: 'white',
    },
    grey:{
      backgroundColor: '#d7d7d7',
      color: 'black',
    },
    red:{
      color: 'red',
    },
    lightGrey:{
      backgroundColor: '#ebebeb',
      color: 'black',
    },
    ultraLightGrey:{
      backgroundColor: '#f4f4f4',
      color: 'black',
    },
    action:{
      padding: '0',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    },
    fixed:{
      position: 'absolute',
      right: '0',
      top: '0'
    },
    buttonToggle:{
      borderRadius: '0px !important',
      padding: '.25rem .5rem',
      border: 'none',
      fontSize: '0.8rem',
      backgroundColor: '#f4f4f4',
      color: 'black',
      width: '100%',
      textWrap: 'wrap',
      '::after':{
        display: 'inline-block',
        marginLeft: '.255em',
        verticalAlign: '0',
        content: '"···"',
        border: 'none'
      }
    },
    startTitle:{
      textTransform: 'uppercase',
      fontSize: '0.6rem'
    },
    document: {
      display: 'flex'
    },
    borderRight: {
      ':first-of-type':{
        borderRight: 'solid #fff 1px;'
      },
      ':last-of-type':{
        borderRight: 'none'
      }
    },
    borderTopBottom: {
      borderTop: 'solid #fff 1px;',
      borderBottom: 'solid #fff 1px;'
    },
    loader: {
      position: 'absolute',
      top: '0',
      right: '0',
      left: '0',
      bottom: '0',
      backgroundColor: 'white',
      opacity : 0.8,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      textTransform: 'uppercase'
    },
    multipresta: {
      position: 'absolute',
      top: '0',
      left: '0',
      color: 'white',
      paddingRight: '0.5rem',
      backgroundColor: themeColor(),
    }
  }
  
  return (
    <>
    <Container className={css(styles.delivery)} fluid>
      <Row className={css(styles.verticalAlign)}>
        <Col xs={12} md={4} className={css(styles.col, styles.borderBottomMobile)}>
        {order?.info?.groupedOrderNumber && <div className={css(styles.multipresta)}><small>{order?.info?.groupedOrderNumber && order.info.groupedOrderNumber}</small></div>}
          <p className={css(styles.para)}>
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => {
                if (e.target.checked) {
                  handleSelectOrder(order);
                } else {
                  handleDeselectOrder(order.id);
                }
              }}
            /> <small><b>{order.info.orderNumber} et {order?.delivery?.info?.numbering}</b></small>
          </p>
          <p className={css(styles.company)}>{parentGroupedOrder?.is_tender && <small style={{color: 'black', backgroundColor: '#f5baed', padding: '2px 4px', borderRadius: '4px'}}>AO</small>} {company && company.name ? company.name : company_old} <br/> {firstname} {lastname}</p>
        </Col>
        <Col xs={6} md={2} className={css(styles.col)}>
          <p className={css(styles.para, styles.center)}>{moment(delivery_date).format('DD/MM/YYYY')}</p>
          <div className={css(styles.divisor, styles.mobileDivisor)}></div>
          <p className={css(styles.para, styles.center)}>{format === 'mealtray' ? 'Plateaux repas' : format}</p>
          <div className={css(styles.divisor, styles.mobileDivisor)}></div>
          <div className={css(styles.para, styles.center)}>
            <span className={css(styles.label2Col, styles.center)}>{participants_number} pers</span>
            {/* {!cancelDate &&
              <span className={css(styles.checkbox2Col, styles.center)}>
                <UpdateDataApi
                  value={confirmed_participants_number}
                  id={id} name={'confirmed_participants_number'}
                  api={order?.grouped_order_id ? 'v2' : info.order_type}
                  input={'checkbox'}
                  handler={() => {}} />
              </span>
            } */}
          </div>
        </Col>
        <Col xs={6} md={2} className={css(styles.col)}>
          <div className={css(styles.para, styles.center)}>
            <span className={css(styles.startTitle)}>Début prestation</span><br/>
            {isCancelled ? 
              info.start_at
            :
              <UpdateDataApi value={info.start_at} id={id} typeFormat="stringify" name={'{"delivery":{"start_at":"value"}}'} api={order?.grouped_order_id ? 'v2' : info.order_type} input={'times'} handler={() => {}}/>
            }

          </div>
          <div className={css(styles.divisor, styles.mobileDivisor)}></div>
          
          <div className={css(styles.para, styles.center)}>
            {isCancelled ? 
                <>Commande annulée le {moment(cancelDate).format('D/M/Y à HH:mm')}</>
              :
              <>
                 {order?.info?.groupedOrderNumber && <small><FontAwesomeIcon icon={faInfoCircle} color="orange"/> Statut commun à tout {order?.info?.groupedOrderNumber}</small>}
                <Select label={''} value={status} handleChange={updateStatus} data={status == 103 ? statusOptionsInvoiced : statusOptions } color={status === '101' && 'orange'}/>
                {parentGroupedOrder?.status === '103' && (() => {
                  if (parentGroupedOrder?.company?.specific_billing) {
                    return <small style={{color: 'orange'}}>Facturation spécifique, pas de passage auto</small>;
                  }
                  if (parentGroupedOrder?.sav_in_progress) {
                    return <small style={{color: 'orange'}}>SAV en cours, pas de passage auto</small>;
                  }
                  if (parentGroupedOrder?.is_tender && !parentGroupedOrder?.customer_special_reference) {
                    return <small style={{color: 'red'}}>Réf. manquante, passage en auto impossible</small>;
                  }
                  return <small style={{color: 'green'}}>Passage auto en "A payer" le {moment(order?.grouped_order_max_delivery_date).add(5, 'days').format("DD/MM/YYYY")}</small>;
                })()}
              </>
            }
          </div>
          
        </Col>
        <Col xs={12} md={2} className={css(styles.col, styles.borderTopMobile)}>
          <p className={css(styles.para, styles.center)}>
            Horaire livraison :<br/>
            {order.delivery.arrived_at_start ? moment(order.delivery.arrived_at_start).format('HH:mm') : "NC"} / {order.delivery.arrived_at_end ? moment(order.delivery.arrived_at_end).format('HH:mm') : "NC"} 
          </p>
          <div className={css(styles.divisor, styles.mobileDivisor)}></div>
          <div className={css(styles.para, styles.center)}>
            <span className={css(styles.label2Col, styles.center)}>
              <small><FontAwesomeIcon icon={faEdit} /></small> {moment(updated_at).format('DD/MM/YYYY HH:mm')}
            </span>
            {/* {!cancelDate &&
              <span className={css(styles.checkbox2Col, styles.center)}>
                <UpdateDataApi
                  id={id}
                  name={'modified_since_last_printed'}
                  api={order?.grouped_order_id ? 'v2' : info.order_type}
                  input={'checkbox'}
                  value={modified_since_last_printed}
                  handler={() => {}} />
                {modified_since_last_printed && <FontAwesomeIcon icon={faExclamationTriangle} color="red"/>}
              </span>
            } */}
          </div>
        </Col>
        <Col xs={12} md={2} className={css(styles.action)}>
          {!cancelDate &&
            <>
              {info.editionVersion === 'v2' ? 
                <Link  to={{
                  pathname: order?.grouped_order_id ? `/commande` : `/dej_multi_format`,
                  search: `${window.location.search}&order_id=${order?.grouped_order_id ? order.grouped_order_id : order.id}&order_type=${info.order_type}&order_index=${info?.order_index}`
                }} className={css(styles.button, styles.theme)}>{status === 103 ? "Vérifier" : "Modifier"}</Link>
              :
              <>
                <Link target="_parent" to={{
                    pathname: `${info.edit_url}?redirect_url_encode=${currentUrlEncode}`,
                  }} className={css(styles.button, styles.theme)}>{status == 103 ? "Vérifier" : "Modifier"}</Link>
                <Link target="_parent" to={{
                    pathname: `${info.clone_url}?redirect_url_encode=${currentUrlEncode}`,
                  }} className={css(styles.button, styles.grey, styles.borderTopBottom)}>Dupliquer</Link>
              </>
              }
            </>
          }
          <Dropdown>
            <Dropdown.Toggle variant="secondary" size="sm" id="dropdown-custom-components" className={css(styles.buttonToggle)}>
              Documents & Annulation
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {info.document_urls.length > 0 ?
                info.document_urls.map((document, index) => 
                  <Dropdown.Item
                    key={index*10}
                    href={document.url}
                    target="_blank"
                  >
                    {document.name}
                  </Dropdown.Item>
                )  
                :
                <Dropdown.Item eventKey="10" href={info.generate_document_url} target="_blank">Générer le document manquant</Dropdown.Item>            
              }
              {!cancelDate &&
                <>
                  <hr/>
                  <Dropdown.Item eventKey="1" href={info.quotation_url} target="_blank">Fiche de production</Dropdown.Item>
                  {SHOW_LEGACY_LMVP &&
                    <>
                      <Dropdown.Item eventKey="2" href={info.propose_url} target="_blank">LMVP (court)</Dropdown.Item>
                      <Dropdown.Item eventKey="3" href={info.propose_long_url} target="_blank">LMVP (long)</Dropdown.Item>
                    </>
                  }
                  <Dropdown.Item eventKey="lmvp-v2" onSelect={() => openLmvp(info.propose_v2_url, order?.grouped_order_id)}>LMVP 2026</Dropdown.Item>
                  <Dropdown.Item eventKey="4" href={`/feuille-de-route/${order.id}`} target="_blank">Feuille de route</Dropdown.Item>
                  <Dropdown.Item eventKey="menu" href={`/menu-a-imprimer/${order.id}`} target="_blank">Menu à imprimer</Dropdown.Item>
                  <Dropdown.Item eventKey="5" href={`/planning?date=${moment(order?.delivery?.delivery_date).format("YYYY-MM-DD")}`} target="_blank">Voir livraison {order?.delivery?.info?.numbering}</Dropdown.Item>
                  <Dropdown.Item eventKey="recap" onSelect={handleOpenRecapModal}>Envoyer récap</Dropdown.Item>
                  {hasClientPlanning &&
                    <Dropdown.Item eventKey="planning-client" href={`/planning-client/${order.id}`} target="_blank">Planning client (alpha)</Dropdown.Item>
                  }
                </>
              }
              <hr/>
              {!cancelDate ?
                <Dropdown.Item 
                  eventKey="4"
                  className={css(styles.red)}
                  onSelect={cancelOrder}
                >
                  Annuler la commande<br/><small>(en cas d'erreur uniquement)</small>
                </Dropdown.Item>
              :
                <Dropdown.Item 
                  eventKey="4" 
                  className={css(styles.red)}
                  onSelect={uncancelOrder}
                >
                  Désannuler la commande<br/><small>(en cas d'erreur uniquement)</small>
                </Dropdown.Item>
              }
            </Dropdown.Menu>
          </Dropdown>
        </Col>
       
      </Row>
      {isLoading && <div className={css(styles.loader)}>
        <div>Chargement...</div>
      </div>}
      {status == 201 && <div className={css(styles.loader)}>
        <div>Commande facturée<br/>(disponible depuis l'onglet "Facture")</div>
      </div>}
    </Container>
    <Modal show={isRecapModalOpen} onHide={handleCloseRecapModal} centered>
      <Modal.Header closeButton>
        <Modal.Title>Envoyer le récapitulatif</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group controlId="recapEmailContent">
          <Form.Label>Bonjour, </Form.Label>
          <Form.Control
            as="textarea"
            rows={6}
            value={recapEmailContent}
            onChange={(e) => setRecapEmailContent(e.target.value)}
            placeholder="Saisissez le message à envoyer au client..."
            disabled={isSendingRecap}
          />
        </Form.Group>
        <small>Un bouton d'accès à l'ensemble des infos récap sera présent dans l'email
        Vous pouvez le consulter pour contrôle ici :</small><br/>
        <a
          href={recapLink || '#'}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleOpenRecapLink}
          aria-disabled={!recapLink && isGeneratingRecapLink}
        >
          {isGeneratingRecapLink ? 'Génération du lien…' : 'Lien récapitulatif'}
        </a>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCloseRecapModal} disabled={isSendingRecap}>
          Annuler
        </Button>
        <Button variant="primary" onClick={handleSendRecapEmail} disabled={isSendingRecap || !recapEmailContent.trim()}>
          {isSendingRecap ? 'Envoi...' : 'Envoyer'}
        </Button>
      </Modal.Footer>
    </Modal>

    <Modal show={showRefusalModal} onHide={() => setShowRefusalModal(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title>Motif du refus</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>Pour quelle raison ce devis est-il refusé ?</p>
        <Form>
          {REFUSAL_REASONS.map((r) => (
            <Form.Check
              key={r.key}
              type="radio"
              name="refusalReason"
              id={`refusal-${order.id}-${r.key}`}
              label={r.label}
              checked={refusalReason === r.key}
              onChange={() => setRefusalReason(r.key)}
            />
          ))}
          {refusalReason === 'autre' && (
            <Form.Group className="mt-2">
              <Form.Label>Précisez la raison</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={refusalReasonDetail}
                onChange={(e) => setRefusalReasonDetail(e.target.value)}
                placeholder="Raison du refus..."
              />
            </Form.Group>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowRefusalModal(false)}>
          Annuler
        </Button>
        <Button
          variant="danger"
          disabled={!refusalReason || (refusalReason === 'autre' && !refusalReasonDetail.trim())}
          onClick={confirmRefusal}
        >
          Confirmer le refus
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  )

}

Order.propTypes = {
  order: PropTypes.object
}

export default Order
